import { ApolloCache, NormalizedCache, useApolloClient } from '@apollo/client'
import { v1 as uuid } from 'uuid'

import {
  useCreateAnnotationMutation,
  useCreatePreviewAnnotationMutation,
  CreateAnnotationMutation,
  CreatePreviewAnnotationMutation,
  CreateAnnotationMutationVariables,
  GetAnnotationDotQuery,
  GetAnnotationDotQueryVariables,
  GetAnnotationDotDocument,
  AnnotationSubjectCountersFragment,
} from '@sketch/gql-types'

import {
  createAnnotationCommentsQuery,
  createCommentFragment,
  createInitialUserQuery,
  hasOptimisticResponseVariables,
} from '../utils'

import { useAnnotationQueryVariables } from '../context'
import { createNewAnnotationInCache } from '../utils/annotationCacheOperations'
import {
  getOptimisticArtboardSubjectFromInputSubject,
  getOptimisticSubjectFromInputSubject,
  isArtboardAnnotationSubjectInput,
} from '../utils/cache'
import { VariablesWithAdditionalParameters } from '../types'
import { usePreview } from '../../shares/views/PreviewView/PreviewContext'

interface CreateAnnotationParams {
  onCacheUpdated?: (data: CreateAnnotationMutation) => void
  onComplete?: (
    data: CreateAnnotationMutation | CreatePreviewAnnotationMutation
  ) => void
}

const useCreateAnnotation = (params?: CreateAnnotationParams) => {
  const apollo = useApolloClient()
  const previewContext = usePreview()
  const isPreview = !!previewContext

  const [createAnnotation] = useCreateAnnotationMutation({
    onError: 'show-toast',
    onCompleted: params?.onComplete,
  })
  const [createPreviewAnnotation] = useCreatePreviewAnnotationMutation({
    onError: 'show-toast',
    onCompleted: params?.onComplete,
  })

  const { versionIdentifier } = useAnnotationQueryVariables('default')

  return <T extends CreateAnnotationMutationVariables>(
    ...args: VariablesWithAdditionalParameters<T>
  ) => {
    const [variables, additionalParameters] = args
    const annotationMockIdentifier = uuid()

    if (!variables) {
      return
    }

    const user = createInitialUserQuery(apollo)

    const { body, shareIdentifier, coordinates, subject } = variables!

    const update = (
      _cache: ApolloCache<NormalizedCache>,
      createAnnotationData: any
    ) => {
      const { data } = createAnnotationData
      let annotation = data?.createAnnotation.annotation

      if (!annotation) {
        // If there's no annotation response no need to update anything then
        return
      }

      /**
       * We are forcing apollo to a write a annotation
       * to the cache with the mocked identifier with the real data
       * (and with the optimistic as well). So when passing from the optimistic
       * to the real data apollo doesn't trigger a request
       *
       * Check the PR description to learn more.
       */
      apollo.cache.writeQuery<
        GetAnnotationDotQuery,
        GetAnnotationDotQueryVariables
      >({
        data: { __typename: 'RootQueryType', annotation },
        query: GetAnnotationDotDocument,
        variables: { identifier: annotationMockIdentifier },
      })

      // For preview annotations, add page field to Artboard if missing (cache queries expect it)
      if (
        isPreview &&
        annotation.currentSubject?.__typename === 'Artboard' &&
        !annotation.currentSubject.page
      ) {
        annotation = {
          ...annotation,
          currentSubject: {
            ...annotation.currentSubject,
            page: {
              __typename: 'Page',
              identifier: uuid(),
              uuid: previewContext?.currentPage?.uuid || uuid(),
            },
          },
        }
      }

      // For preview annotations, add top-level page field if missing (cache queries expect it)
      if (isPreview && !annotation.page) {
        const pageIdentifier =
          annotation.currentSubject?.__typename === 'Artboard'
            ? annotation.currentSubject.page?.identifier
            : annotation.currentSubject?.__typename === 'Page'
              ? annotation.currentSubject.identifier
              : undefined

        // Extract permanentPageId from subject if it exists (for preview artboard annotations)
        const variableSubject = variables.subject
        const subjectPageId =
          variableSubject && 'permanentPageId' in variableSubject
            ? (variableSubject as any).permanentPageId
            : undefined

        const pageUuid = subjectPageId || previewContext?.currentPage?.uuid

        // Skip adding page if we don't have the required uuid
        if (!pageUuid) {
          return
        }

        const pageObject: AnnotationSubjectCountersFragment['page'] = {
          __typename: 'Page',
          identifier: pageIdentifier || pageUuid,
          uuid: pageUuid,
          pageUUID: pageUuid, // Alias used in AnnotationPageInfo fragment
        }

        annotation = {
          ...annotation,
          page: pageObject,
        }
      }

      createNewAnnotationInCache(
        apollo,
        {
          shareIdentifier: shareIdentifier || '',
          versionIdentifier: versionIdentifier || '',
        },
        annotation,
        isPreview
      )

      // Create the annotation query on the cache
      // We will use it to represent the annotation right away
      // on the floating annotations
      createAnnotationCommentsQuery(
        apollo,
        annotation,
        annotation.share.identifier
      )

      params?.onCacheUpdated?.(data)
    }

    if (!hasOptimisticResponseVariables(variables)) {
      throw new Error(
        'Missing variables in optimistic update (`createAnnotation` mutation)'
      )
    }

    const comment = createCommentFragment(body, user.me)

    // For preview artboard annotations, permanentPageId is in the subject, not additionalParameters
    const subjectAsAny = subject as any
    const permanentPageId =
      additionalParameters?.permanentPageId ||
      subjectAsAny.permanentPageId ||
      ''

    const currentSubject = isArtboardAnnotationSubjectInput(subject)
      ? getOptimisticArtboardSubjectFromInputSubject(
          subject,
          permanentPageId || ''
        )
      : getOptimisticSubjectFromInputSubject(subject)

    if (isPreview) {
      // Preview annotations don't return the page field from server, but cache queries expect it
      // So we add a minimal page object for cache compatibility
      const previewCurrentSubject =
        currentSubject.__typename === 'Artboard'
          ? {
              __typename: 'Artboard' as const,
              identifier: uuid(),
              documentVersionShortId: currentSubject.documentVersionShortId,
              permanentArtboardShortId: currentSubject.permanentArtboardShortId,
              uuid: currentSubject.uuid,
              page: currentSubject.page || {
                __typename: 'Page' as const,
                identifier: uuid(),
                uuid:
                  additionalParameters?.permanentPageId ||
                  previewContext?.currentPage?.uuid ||
                  uuid(),
              },
            }
          : currentSubject

      // Build page object for optimistic response
      // For preview annotations, use permanentPageId as the page UUID
      const pageUuid = permanentPageId || previewContext?.currentPage?.uuid
      const pageIdentifier =
        previewCurrentSubject.__typename === 'Artboard'
          ? previewCurrentSubject.page.identifier
          : previewCurrentSubject.__typename === 'Page'
            ? previewCurrentSubject.identifier
            : pageUuid

      const optimisticPageObject: AnnotationSubjectCountersFragment['page'] = {
        __typename: 'Page',
        identifier: pageIdentifier || pageUuid || uuid(),
        uuid: pageUuid || uuid(),
        pageUUID: pageUuid || uuid(), // Alias used in AnnotationPageInfo fragment
      }

      const optimisticResponse: CreatePreviewAnnotationMutation = {
        __typename: 'RootMutationType',
        createAnnotation: {
          __typename: 'CreateAnnotationResponse',
          annotation: {
            __typename: 'Annotation',
            isNew: false,
            hasNewComments: false,
            identifier: annotationMockIdentifier,
            subscriptionStatus: 'ON',
            isOptimistic: true,

            comments: {
              __typename: 'Comments',
              meta: {
                __typename: 'PaginationMeta',
                totalCount: 1,
              },
            },
            firstComment: comment,
            latestCommentCreatedAt: comment.createdAt,

            currentSubject: previewCurrentSubject,

            share: {
              __typename: 'Share',
              identifier: shareIdentifier,
              subscriptionStatus: 'PARTIAL',
            },
            page: optimisticPageObject,
            coordinates: {
              __typename: 'AnnotationCoordinates',
              identifier: uuid(),
              x: coordinates?.x || 0,
              y: coordinates?.y || 0,
            },

            resolution: null,
          },
        },
      }

      const frameUuid =
        subject.type === 'ARTBOARD' ? subject.permanentId : undefined

      return createPreviewAnnotation({
        update,
        optimisticResponse,
        variables: {
          ...variables,
          previewIdentifier: previewContext.preview.identifier,
          pageUuid: previewContext.currentPage?.uuid,
          frameUuid,
        },
      })
    }

    const optimisticResponse: CreateAnnotationMutation = {
      __typename: 'RootMutationType',
      createAnnotation: {
        __typename: 'CreateAnnotationResponse',
        annotation: {
          __typename: 'Annotation',
          isNew: false,
          hasNewComments: false,
          identifier: annotationMockIdentifier,
          subscriptionStatus: 'ON',
          isOptimistic: true,

          comments: {
            __typename: 'Comments',
            meta: {
              __typename: 'PaginationMeta',
              totalCount: 1,
            },
          },
          firstComment: comment,
          latestCommentCreatedAt: comment.createdAt,

          currentSubject,

          share: {
            __typename: 'Share',
            identifier: shareIdentifier,
            subscriptionStatus: 'PARTIAL',
          },
          page: {
            __typename: 'UnprocessedPage',
          },
          coordinates: {
            __typename: 'AnnotationCoordinates',
            identifier: uuid(),
            x: coordinates?.x || 0,
            y: coordinates?.y || 0,
          },

          resolution: null,
        },
      },
    }

    return createAnnotation({
      update,
      optimisticResponse,
      variables,
    })
  }
}

export default useCreateAnnotation
